import {
  Button,
  CircularProgress,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { showings } from '@prisma/client';
import { isBefore } from 'date-fns';
import showingsService from '../../api/services/showings';
import { useAppDispatch } from '../../store/store';
import { fetchShowings } from '../../store/slices/showings';
import Circle from '../Circle/Circle';
import ShowingAgent, {
  AGENT_AWAITING_CONFIRMATION_LABEL,
  isAgentAwaitingConfirmation,
} from '../ShowingAgent/ShowingAgent';
import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import formatTime from '../../utils/formatTime';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DateTimeSelector from '../DateTimeSelector/DateTimeSelector';
import useIsMobile from '../../hooks/useIsMobile';

interface Props {
  showing?: showings;
  vertical?: boolean;
  subVertical?: boolean;
  admin?: boolean;
  /** Suppress the agent row when the surrounding surface renders it itself. */
  hideAgent?: boolean;
}

const ShowingTime: React.FC<Props> = ({
  showing,
  vertical,
  subVertical,
  admin,
  hideAgent = false,
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();
  const { currentUser } = useAuth();
  const [showingAgentCanBook, setShowingAgentCanBook] = useState(true);
  const [denyModalOpen, setDenyModalOpen] = useState(false);
  const [altTimes, setAltTimes] = useState<Date[]>([new Date()]);
  const [altTimesError, setAltTimesError] = useState<string | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  if (!showing) {
    return <Typography variant="body2">None</Typography>;
  }

  const showDenyModal = (e: any) => {
    e.stopPropagation();
    setDenyModalOpen(true);
  };

  const updateShowing = async (
    e: any,
    confirm: boolean,
    altTimesToSend?: Date[]
  ) => {
    setLoading(true);
    e.stopPropagation();

    const patchData: any = {
      accepted: confirm,
      agentBooks: showingAgentCanBook,
    };
    if (!confirm && altTimesToSend && altTimesToSend.length > 0) {
      patchData.suggestedTimes = altTimesToSend;
    }

    await showingsService.patch(showing?.id, patchData);
    setLoading(false);
    dispatch(fetchShowings());
  };

  const cancelShowing = async (e: any, reason?: string) => {
    setLoading(true);
    e.stopPropagation();

    await showingsService.patch(showing?.id, {
      accepted: null,
      ...(reason && reason.trim() ? { cancellationReason: reason.trim() } : {}),
    });
    setLoading(false);
    dispatch(fetchShowings());
  };

  const showingTime = new Date(showing.proposedTime);
  const showingText = formatTime(showingTime);
  const cancelled = showing.accepted === null || !!showing.cancelledAt;
  const showingPending = showing.accepted === false && !cancelled;
  const completed =
    !cancelled && showing.accepted && isBefore(showingTime, new Date());
  const agentAwaitingConfirmation = isAgentAwaitingConfirmation(showing);

  let text = 'Accepted';
  if (agentAwaitingConfirmation) {
    text = AGENT_AWAITING_CONFIRMATION_LABEL;
  } else if (showingPending) {
    text = 'Pending';
  } else if (completed) {
    text = 'Completed';
  } else if (cancelled) {
    text = 'Cancelled';
  }

  // The assigned agent stays on the card after the showing completes — who
  // walked the property is still the answer to "who showed it?". A cancelled
  // showing hides it: the assignment no longer stands. An agent who has
  // accepted but not yet confirmed is shown too, so the buyer knows who to
  // expect.
  const showAgentRow =
    !hideAgent &&
    !cancelled &&
    !!showing.showingAgentName &&
    (showing.accepted === true || agentAwaitingConfirmation);

  return (
    <Stack
      direction={vertical ? 'column' : 'row'}
      spacing={1}
      alignItems={vertical ? 'flex-start' : 'center'}
    >
      <Stack direction={subVertical ? 'column' : 'row'} spacing={1}>
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
          {!completed && !cancelled && (
            <Circle
              size={15}
              color={showingPending ? '#f1c40f' : 'success.main'}
            />
          )}
          {cancelled && <Circle size={15} color="error.main" />}
          <Typography variant="caption" fontWeight={600}>
            {text}
          </Typography>
        </Stack>
        <Typography variant="body2">{showingText}</Typography>
        {loading && <CircularProgress size={20} />}
      </Stack>
      {showAgentRow && <ShowingAgent showing={showing} />}
      {/* Only show the buttons if the user is the seller and the showing is pending */}
      {showingPending && !loading && currentUser?.id !== showing.userId && (
        <>
          <Button
            variant="outlined"
            color="error"
            onClick={(e) => admin ? updateShowing(e, false) : showDenyModal(e)}
            fullWidth={vertical}
          >
            Deny
          </Button>
          <Button
            variant="contained"
            color="success"
            sx={{ color: 'success.contrastText' }}
            onClick={(e) => updateShowing(e, true)}
            fullWidth={vertical}
          >
            Confirm
          </Button>
          {admin && (
            <FormControlLabel
              control={
                <Switch
                  checked={showingAgentCanBook}
                  onChange={(e) => setShowingAgentCanBook(e.target.checked)}
                  color="primary"
                  disabled={loading}
                />
              }
              label="Agent Books"
              sx={{ mb: 1 }}
            />
          )}
          <Dialog
            open={denyModalOpen}
            onClose={() => setDenyModalOpen(false)}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
          >
            <DialogTitle>Suggest Other Times</DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Typography variant="body2">
                  You must suggest at least one other available time for this
                  showing request.
                </Typography>
                {altTimes.map((time, idx) => (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    key={idx}
                  >
                    <DateTimeSelector
                      value={time}
                      onChange={(newValue) => {
                        const updated = [...altTimes];
                        updated[idx] = newValue as Date;
                        setAltTimes(updated);
                      }}
                      minDate={new Date()}
                      showLabel={false}
                      fullWidth
                    />
                    {altTimes.length > 1 && (
                      <IconButton
                        onClick={() => {
                          setAltTimes(altTimes.filter((_, i) => i !== idx));
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setAltTimes([...altTimes, new Date()])}
                  sx={{ mt: 1 }}
                >
                  Add Another Time
                </Button>
                {altTimesError && (
                  <Typography color="error" variant="body2">
                    {altTimesError}
                  </Typography>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDenyModalOpen(false)} color="inherit">
                Cancel
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={async (e) => {
                  // Filter out invalid/empty times
                  const validTimes = altTimes.filter(Boolean);
                  if (!validTimes.length) {
                    setAltTimesError(
                      'You must suggest at least one valid time.'
                    );
                    return;
                  }
                  setAltTimesError(null);
                  setDenyModalOpen(false);
                  await updateShowing(e, false, validTimes);
                }}
              >
                Deny & Send Times
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      {/* Only show the cancel button if the user is the buyer and the showing is accepted */}
      {showing.accepted &&
        !completed &&
        !cancelled &&
        !loading &&
        currentUser?.id === showing.userId && (
          <Button
            variant="outlined"
            color="error"
            onClick={(e) => cancelShowing(e)}
          >
            Cancel
          </Button>
        )}
      {/* Admins can cancel an accepted showing and provide a reason that is sent
          to the seller and the requesting agent/buyer. */}
      {admin &&
        showing.accepted &&
        !completed &&
        !cancelled &&
        !showing.cancelledAt &&
        !loading && (
        <>
          <Button
            variant="outlined"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              setCancelModalOpen(true);
            }}
            fullWidth={vertical}
          >
            Cancel
          </Button>
          <Dialog
            open={cancelModalOpen}
            onClose={() => setCancelModalOpen(false)}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
          >
            <DialogTitle>Cancel Showing</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Provide a reason for cancelling this showing. It will be
                  included in the cancellation notification sent to the seller
                  and the requesting agent.
                </Typography>
                <TextField
                  label="Reason"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setCancelModalOpen(false)}
                color="inherit"
              >
                Back
              </Button>
              <Button
                color="error"
                variant="contained"
                disabled={!cancelReason.trim()}
                onClick={async (e) => {
                  setCancelModalOpen(false);
                  await cancelShowing(e, cancelReason);
                  setCancelReason('');
                }}
              >
                Cancel Showing
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Stack>
  );
};

export default ShowingTime;
